<template>
    <VJoyTemplate class="billing-information__wrapper">
        <template #template-title>
            <div>{{ t('projectclient.billinginformation.navstep.confirm') }}</div>
        </template>
        <template v-if="startDay" #template-subtitle>
            <p>{{ startDay }}</p>
        </template>
        <template #template-main>
            <form :id="formId" class="billing-information" @submit.prevent>
                <div class="billing-information__body" :class="{isMobileScreen}">
                    <div class="billing-information__wrapper">
                        <form :id="formId" class="billing-information__form" :class="{isMobileScreen}" @submit.prevent>
                            <div v-if="projectInformation.projectPeriodRequired">
                                <h3>{{ t('projectclient.billinginformation.projectperiod') }}</h3>
                                <div class="calendar-inputs / flex">
                                    <div @click.stop>
                                        <MaltDatePicker
                                            key="startDate"
                                            v-model="modelValue.periodStartDate"
                                            :label="t('projectclient.billinginformation.projectperiod.startdate')"
                                            name="startDate"
                                            :invalid="r$.periodStartDate?.$error"
                                        />
                                        <ValidationError :errors="r$.periodStartDate?.$errors" />
                                    </div>
                                    <div @click.stop>
                                        <MaltDatePicker
                                            key="endDate"
                                            v-model="modelValue.periodEndDate"
                                            :label="t('projectclient.billinginformation.projectperiod.enddate')"
                                            name="endDate"
                                            :invalid="r$.periodEndDate?.$error"
                                        />
                                        <ValidationError :errors="r$.periodEndDate?.$errors" />
                                    </div>
                                </div>
                            </div>
                            <h3 class="section-headline">{{ t('projectclient.billinginformation.invoice') }}</h3>

                            <!-- Purchase order number -->
                            <VJoyInput
                                v-model="modelValue.purchaseOrderNumber"
                                :label="t('projectclient.billinginformation.invoice.purchaseorder')"
                                placeholder="PO-12345"
                                name="purchaseOrder"
                                data-testid="billing-information-purchase-order"
                            ></VJoyInput>
                            <ValidationNotification
                                v-for="tooltip in r$.purchaseOrderNumber?.$tooltips"
                                :key="tooltip"
                                :label="tooltip"
                                type="neutral"
                            />
                            <ValidationError :errors="r$.purchaseOrderNumber?.$errors" />

                            <div v-if="canAddExtraDocument">
                                <h3 class="section-headline">
                                    {{ t('projectclient.billinginformation.additionaldocuments') }}
                                </h3>
                                <VJoyDropzone
                                    v-model="additionalDocument"
                                    data-testid="project-validation-billing-information-upload-dropzone"
                                    name="additionalDocument"
                                    :accept="['application/pdf']"
                                >
                                    <template #dropzone-description>
                                        {{ $t('projectclient.billinginformation.additionaldocuments.upload.description') }}
                                    </template>
                                </VJoyDropzone>
                            </div>

                            <div v-else-if="projectInformation.lot.extraDocumentName">
                                <h3 class="section-headline">
                                    {{ t('projectclient.billinginformation.additionaldocuments') }}
                                </h3>
                                <p>{{ projectInformation.lot.extraDocumentName }}</p>
                            </div>
                        </form>
                        <InjectMicroFrontendApp
                            app="compliance-mf-billing-information"
                            :props="{
                                companyId: projectInformation.client.companyId,
                                layoutVariant: 'complete',
                                showLegalInformationStatus: true,
                                showSaveButton: false,
                                page: 'timesheet_page',
                                readOnlyMode: isEnterpriseClient ? 'if-filled' : 'if-filled-and-match',
                                invalidateOnVatMismatch: true,
                            }"
                            class="billing-information-block"
                            @billing-information-updated-successfully="onBillingInformationUpdatedSuccessfully"
                            @billing-information-update-failed="onBillingInformationUpdateFailed"
                            @billing-information-filled-state-changed="onBillingInformationFilledStateChanged"
                        >
                            <template #loading>
                                <BillingInformationSkeleton />
                            </template>
                        </InjectMicroFrontendApp>
                        <InjectMicroFrontendApp
                            v-if="projectInformation.client.companyId && additionalBillingProfileFfEnabled"
                            class="additional-billing-profile-block"
                            app="compliance-mf-additional-billing-profile"
                            :props="{legalEntityId: projectInformation.client.companyId, mode: 'selection'}"
                            @billing-profile-selected="onBillingProfileSelected"
                        >
                            <template #loading>
                                <AdditionalBillingProfileSelectionSkeleton />
                            </template>
                        </InjectMicroFrontendApp>
                        <div class="billing-information-block">
                            <template v-if="isClientCompanyAdmin && isBillingContactsWriteable">
                                <!-- Billing contact -->
                                <VJoyInput
                                    v-model="modelValue.billingContact"
                                    :label="t('projectclient.billinginformation.invoice.billing.contact')"
                                    name="billingContact"
                                    data-testid="billing-information-contact-email"
                                ></VJoyInput>
                                <ValidationError :errors="r$.billingContact?.$errors" />

                                <!-- Tags -->
                                <VJoyTagsInput
                                    v-model="modelValue.otherBillingContacts"
                                    :values="modelValue.otherBillingContacts"
                                    :placeholder="t('projectclient.billinginformation.additional.billing.placeholder')"
                                    validation="email"
                                    data-testid="billing-information-other-contact-emails"
                                    :invalid-email-error="t('projectclient.general.forms.errors.allFieldsEmails')"
                                >
                                    <p class="label">
                                        {{ t('projectclient.billinginformation.invoice.additional.billing.contacts') }}
                                    </p>
                                </VJoyTagsInput>
                            </template>
                            <ReadOnlyInvoicingContacts v-else :billing-infos="modelValue" :company-id="projectInformation.client.companyId" />
                            <VJoyTagsInput
                                v-model="modelValue.otherBillingContacts"
                                :values="modelValue.otherBillingContacts"
                                :placeholder="t('projectclient.billinginformation.additional.billing.placeholder')"
                                validation="email"
                                :invalid-email-error="t('projectclient.general.forms.errors.allFieldsEmails')"
                            >
                                <p class="label">
                                    {{ t('projectclient.billinginformation.invoice.project.billing.contacts') }}
                                </p>
                            </VJoyTagsInput>
                        </div>
                    </div>
                    <ProjectSummary :project-information="projectInformation" />
                </div>
            </form>
            <p style="margin-top: 10px">
                {{ t('projectclient.activitytracker.more.details.contact') }}
                <VJoyLink target="_blank" :href="conversationUrl" :text="`${projectInformation?.freelancer?.fullName}`" />
            </p>
            <p class="mission-confirmation">
                {{
                    t(clientAgreementTranslationKey, {
                        0: projectInformation.freelancer?.fullName,
                        1: formatAmount(projectInformation.contract.currency, projectInformation.lot.invoiceDetails.totalIncludingTaxes, {
                            decimals: 2,
                        }),
                    })
                }}
            </p>
        </template>
    </VJoyTemplate>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useProjectInformationStore, useScreenSizeStore} from '@common/stores';
    import {BillingInformationSkeleton, AdditionalBillingProfileSelectionSkeleton, ValidationError, ValidationNotification} from '@common/ui';
    import ReadOnlyInvoicingContacts from '@features/validation/ui/BillingInformation/ReadOnlyInvoicingContacts.vue';
    import type {RegleRoot} from '@regle/core';
    import {storeToRefs} from 'pinia';
    import {InjectMicroFrontendApp, MaltDatePicker} from '#components';
    import {onBeforeUnmount, onMounted, useFormat, useLogger, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {escapeHtml} from '@malt/sanitize';
    import {type IJoynackbarServiceReturn, pushVJoySnackbar, VJoyDropzone, VJoyInput, VJoyLink, VJoyTagsInput, VJoyTemplate} from '@maltjoy/core-vue';
    import type {ClientCompanyBillingInfoParam, ProjectValidationParam} from '@api';
    import {ProjectSummary} from '../ProjectSummary';

    const logger = useLogger();
    const {formatAmount} = useFormat();
    const additionalBillingProfileFfEnabled = useFeatureFlag('compliance-additional-billing-profile-mf');

    const {t} = useTranslation();

    const clientAgreementTranslationKey = computed(() =>
        projectInformation.value.clientBelongsToAWalletCompany
            ? 'projectclient.billinginformation.agreement.for.wallet.companies'
            : 'projectclient.billinginformation.agreement',
    );

    const conversationUrl = computed(() => `/messages/${projectInformation.value?.contract?.conversationId}`);
    const modelValue = defineModel<ClientCompanyBillingInfoParam & ProjectValidationParam>({required: true});
    const additionalDocument = defineModel<File[]>('additionalDocument', {required: true});

    const props = defineProps<{
        startDay?: string;
        isValidationBlocked: boolean;
        r$: RegleRoot<ClientCompanyBillingInfoParam & ProjectValidationParam>;
        formId: string;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: ClientCompanyBillingInfoParam & ProjectValidationParam];
        'billing-information-updated-successfully': [];
        'billing-information-update-failed': [failureReason: 'INVALID_BILLING_INFORMATION' | 'INSUFFICIENT_PERMISSIONS' | 'SERVER_ERROR'];
        'billing-information-filled-state-changed': [state: boolean];
    }>();

    // screen size store
    const screenSizeStore = useScreenSizeStore();
    const {isMobileScreen} = storeToRefs(screenSizeStore);

    // projectInformation store
    const projectInformationStore = useProjectInformationStore();
    const {projectInformation, isClientCompanyAdmin, isEnterpriseClient} = storeToRefs(projectInformationStore);

    // display error notification if validation is blocked
    const {validationInfo} = projectInformation.value;

    const snackbarInstances: IJoynackbarServiceReturn['snackbarApp'][] = [];

    const isBillingContactsWriteable = !modelValue.value.billingContact;

    const onBillingInformationUpdatedSuccessfully = () => {
        emit('billing-information-updated-successfully');
    };
    const onBillingInformationUpdateFailed = (failureReason: 'INVALID_BILLING_INFORMATION' | 'INSUFFICIENT_PERMISSIONS' | 'SERVER_ERROR') => {
        logger.error('[BillingInformation] Save failed, validation will not proceed. Reason:', failureReason);
        emit('billing-information-update-failed', failureReason);
    };
    const onBillingInformationFilledStateChanged = (state: boolean) => {
        emit('billing-information-filled-state-changed', state);
    };

    const onBillingProfileSelected = (profileId: string | null) => {
        modelValue.value.selectedBillingProfileId = profileId ?? undefined;
    };

    onMounted(() => {
        if (props.isValidationBlocked) {
            validationInfo?.projectValidationRequirement?.forEach((requirement) => {
                const {snackbarApp} = pushVJoySnackbar({
                    props: {
                        header: t(`projectclient.billinginformation.approval.blocked.header.${requirement}`),
                        message: t(`projectclient.billinginformation.approval.blocked.message.${requirement}`, {
                            0: escapeHtml(
                                requirement === 'PRE_PAYMENT_NOT_PAID'
                                    ? projectInformation.value.projectId
                                    : projectInformation.value.freelancer.fullName,
                            ),
                        }),
                        level: 'error',
                        closable: false,
                        duration: 'forever',
                    },
                });
                snackbarInstances.push(snackbarApp);
            });
        }
    });

    // check additional resource upload authorisation
    const canAddExtraDocument = computed(() => !projectInformation.value.lot.extraDocumentId && !projectInformation.value.lot.extraDocumentName);

    onBeforeUnmount(() => {
        snackbarInstances.forEach((instance) => instance.unmount());
    });
</script>

<style lang="scss" scoped>
    :deep(.joy-template-slot-title) {
        font-weight: var(--joy-font-weight-bold) !important;
        font-size: var(--joy-font-size-primary-800) !important;
    }

    .billing-information__wrapper {
        padding: 0 var(--joy-core-spacing-4);
        overflow: hidden;
    }

    .billing-information {
        &__body {
            display: flex;
            justify-content: center;

            &.isMobileScreen {
                flex-direction: column;
            }
        }

        &__wrapper {
            flex: 6;
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-6);
        }

        &__form,
        .billing-information-block {
            flex: 6;
            margin-right: var(--joy-core-spacing-12);
            padding: var(--joy-core-spacing-6);
            border-radius: var(--joy-core-radius-3);
            border: 1px solid var(--joy-color-neutral-30);
            background: var(--joy-color-background-default);

            &.isMobileScreen {
                margin: 0;
            }

            :deep(.joy-label),
            .label {
                font-size: var(--joy-font-size-primary-450);
                font-weight: var(--joy-font-weight-bold);
            }

            .section-headline {
                font-size: var(--joy-font-size-primary-600);
                font-weight: var(--joy-font-weight-bold);
            }

            .calendar-inputs {
                gap: var(--joy-core-spacing-4);
                margin-bottom: var(--joy-core-spacing-2);

                &_label {
                    font-weight: var(--joy-font-weight-bold);
                    font-size: var(--joy-font-size-primary-300);
                }

                :deep(.joy-calendar) {
                    margin-top: 0;
                }

                :deep(.joy-input) {
                    line-height: 0;
                }

                & > div {
                    flex: 1;
                }

                & > div > div {
                    margin-top: var(--joy-core-spacing-2);
                }
            }

            .find-address {
                color: var(--joy-color-secondary-30);
                margin: var(--joy-core-spacing-2) 0 var(--joy-core-spacing-6) 0;
            }
        }

        .additional-billing-profile-block {
            flex: 6;
            margin-right: var(--joy-core-spacing-12);
        }

        :deep(a.joy-link.joy-link_teal) {
            text-decoration: underline;
            font-weight: var(--joy-font-weight-normal);
        }
    }

    .mission-confirmation {
        margin-top: var(--joy-core-spacing-6);
        font-size: var(--joy-font-size-primary-300);
        color: var(--joy-color-neutral-40);
    }
</style>
